import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Accordion } from "react-bootstrap";
import { useQueryClient } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";

import { ReactComponent as ChevronDown } from "assets/images/iconsV2/chevron-down-tertiary.svg";
import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import { useGetTrackingUpdates } from "api/queries/shipments/useGetTrackingUpdates";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import ReferencesDialog from "pages/shipper/ShipmentTracking/components/Tracking/components/ReferencesDialog/ReferencesDialog";
import { FEATURE_FLAGS } from "constants/featureFlags";
import useFeatureFlag from "hooks/useFeatureFlag";
import usePollingAction from "hooks/usePollingAction";
import { useGetTrackingReferences } from "api/queries/shipments/useGetTrackingReferences";
import { useTrackingStopAssociation } from "pages/shipper/ShipmentTracking/components/Tracking/components/ReferencesDialog/useTrackingStopAssociation";
import Card from "components/v2/Card/Card";
import Button from "components/v2/Button/Button";
import { ReactComponent as PlusIcon } from "assets/images/iconsV2/plus.svg";
import { ReactComponent as MarkerPinIcon } from "assets/images/iconsV2/marker-pin-01.svg";
import { ReactComponent as FeaturedIcon } from "assets/images/iconsV2/featured-icon-gray.svg";
import EmptyState from "components/v2/EmptyState/EmptyState";
import useCustomBrokerPermission from "hooks/useCustomBrokerPermission";
import usePermissions from "hooks/usePermissions";
import { classNames } from "helpers/commonHelpers";
import AddTrackingNumberModal from "./AddTrackingNumberModal";
import { generateTrackingItems, getTrackingNumberOptions, refreshTrackingData, sortByTimestamp } from "./TrackingUtils";
import TrackingItem, { TrackingItemProps } from "./components/TrackingItem/TrackingItem";
import "./Tracking.scss";

type TrackingProps = {
  /**
   * The shipment ID.
   */
  id: string;
  /**
   * The shipment data.
   */
  shipment: any;
  /**
   * Whether the shipment data is loading.
   */
  isShipmentLoading: boolean;
};

const Tracking: FC<TrackingProps> = function ({ id, shipment, isShipmentLoading }) {
  const intl = useIntl();
  const { startPolling } = usePollingAction();
  const queryClient = useQueryClient();
  const { permissions } = usePermissions();
  const [isCustomBroker] = useCustomBrokerPermission();
  const { user_type } = JSON.parse(permissions || "{}");
  // Shipper settings from shipment itself if user is provider
  const company_shipper_settings = useMemo(() => {
    const { company_shipper_settings: fromPermissions } = JSON.parse(permissions || "{}");
    return fromPermissions || shipment?.settings;
  }, [permissions, shipment?.settings]);
  const {
    data: trackingNumberData,
    isLoading: isLoadingTrackingNumberData,
    isError,
  } = useGetTrackingReferences(id, {
    enabled: !!id,
  });
  const { data: trackingUpdates, isLoading: isLoadingTrackingUpdates } = useGetTrackingUpdates(id, {
    enabled: !!id,
    select: useCallback((data) => sortByTimestamp(data), [id]),
  });
  const { data: shipmentBasicData }: any = useGetShipmentBasic(id, {
    enabled: !!id,
    refetchOnMount: false,
  });
  const { payment_location_reference } = shipmentBasicData?.organization ?? {};
  const isRevampEnabled = useFeatureFlag(FEATURE_FLAGS.WEB_11070_TRACKING_NUMBER_REVAMP);
  const { stops: associatedDeliveryStops } = useTrackingStopAssociation(id);
  const deliveryStops = associatedDeliveryStops.length > 0 ? associatedDeliveryStops : undefined;
  const rawScheduleStops = shipment?.schedule?.schedule_stops ?? shipment?.schedule?.stops;
  const scheduleStops = Array.isArray(rawScheduleStops) ? rawScheduleStops : undefined;

  const [activeAccordion, setActiveAccordion] = useState<string | null>(null);
  const [showRestAccordion, setShowRestAccordion] = useState<boolean>(false);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedTrackingNumberID, setSelectedTrackingNumberID] = useState<number | null>(null); // This is the ID not the tracking number
  const [trackingItems, setTrackingItems] = useState<TrackingItemProps[]>([]);
  const [trackingNumberOptions, setTrackingNumberOptions] = useState<any[]>([]);

  const freightMode = shipment?.service?.freight_mode;
  const isLoading = isLoadingTrackingNumberData || isLoadingTrackingUpdates || isShipmentLoading;
  const hasTrackingItems = trackingItems?.length > 0;
  const firstFiveTrackingItems = (trackingItems ?? []).slice(0, 5);
  const restOfTrackingItems = (trackingItems ?? []).slice(5);
  const hasMoreThanFiveTrackingItems = restOfTrackingItems.length > 0;

  // If there is only one tracking item, expand it by default
  useEffect(() => {
    if (trackingItems?.length === 1) setActiveAccordion(trackingItems[0].uuid);
  }, [trackingItems]);

  // Format API data into TrackingNumberOptions and TrackingItems
  useEffect(() => {
    if (trackingNumberData && Object.keys(shipment).length > 0) {
      const trackingNumberOptions = getTrackingNumberOptions(trackingNumberData, payment_location_reference, {
        excludeBookingReference: isRevampEnabled,
      });
      setTrackingNumberOptions(trackingNumberOptions);

      setTrackingItems(
        generateTrackingItems(
          trackingUpdates?.updates,
          trackingNumberData.references ?? [],
          trackingNumberOptions,
          intl,
          company_shipper_settings,
          { deliveryStops, scheduleStops },
        ),
      );
    }
  }, [
    trackingNumberData,
    trackingUpdates,
    shipment,
    permissions,
    isRevampEnabled,
    payment_location_reference,
    intl,
    company_shipper_settings,
    deliveryStops,
    scheduleStops,
  ]);

  const openEditDialog = (trackingNumberID?: number) => {
    setSelectedTrackingNumberID(trackingNumberID || null);
    setIsDialogOpen(true);
  };

  const openAddDialog = () => {
    setSelectedTrackingNumberID(null);
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
  };

  const handleAccordionSelect = (eventKey: string | null) => {
    if (eventKey) {
      setActiveAccordion(eventKey);
    } else {
      setActiveAccordion(null);
    }
  };

  return (
    <>
      {isRevampEnabled ? (
        <AddTrackingNumberModal
          freightMode={freightMode}
          freightModeType={shipment?.service?.freight_mode_type}
          isOpen={isDialogOpen}
          close={closeDialog}
          prevReferences={trackingNumberData?.references}
          typeOptions={trackingNumberOptions}
          refreshTrackingData={(type) => refreshTrackingData(type, queryClient, id, startPolling)}
          shipmentId={id}
          referenceId={selectedTrackingNumberID}
          company_shipper_settings={company_shipper_settings}
        />
      ) : (
        <ReferencesDialog
          freightMode={freightMode}
          freightModeType={shipment?.service?.freight_mode_type}
          isOpen={isDialogOpen}
          close={closeDialog}
          prevReferences={trackingNumberData?.references}
          typeOptions={trackingNumberOptions}
          refreshTrackingData={(type) => refreshTrackingData(type, queryClient, id, startPolling)}
          shipmentId={id}
          referenceId={selectedTrackingNumberID}
          isBookingReference={false}
          company_shipper_settings={company_shipper_settings}
        />
      )}
      <div>
        <Card borderRadius="md">
          <Card.Header
            title={intl.formatMessage({ id: "tracking.trackingDetails", defaultMessage: "Tracking details" })}
            {...(hasTrackingItems && {
              supportingText: intl.formatMessage({
                id: "tracking.timestamps",
                defaultMessage: "Timestamps are in local time of each location",
              }),
            })}
            className="flex-nowrap"
            showBorder
            actions={
              (user_type === "provider" && !hasTrackingItems) || // In this case, the empty state with Add Tracking number CTA will be shown below instead
              isLoading ||
              isCustomBroker ? null : (
                <div className={classNames({ "h-100 d-flex align-items-center": !hasTrackingItems })}>
                  <Button
                    variant="link-color"
                    label={intl.formatMessage({ id: "tracking.addTrackingNumber", defaultMessage: "Add tracking number" })}
                    iconLeft={<PlusIcon />}
                    onClick={openAddDialog}
                    className="mt-xs"
                    leftIconClassName="add-tracking-number-icon"
                    labelClassName="add-tracking-number-label"
                  />
                </div>
              )
            }
          />
          {isLoading && <LoadingSkeleton rows={3} />}
          {!hasTrackingItems && !isLoading && !isError && (
            <div className="mt-4xl mb-4xl">
              <EmptyState
                title={intl.formatMessage({
                  id: "tracking.noTrackingNumberAdded",
                  defaultMessage: "No tracking number added",
                })}
                description={intl.formatMessage({
                  id: "tracking.addATrackingNumber",
                  defaultMessage: "Add a tracking number to get live shipment updates.",
                })}
                customIcon={<MarkerPinIcon />}
                {...(user_type === "provider" && !isCustomBroker
                  ? {
                      customActions: (
                        <Button
                          label={intl.formatMessage({
                            id: "tracking.addTrackingNumber",
                            defaultMessage: "Add tracking number",
                          })}
                          onClick={openAddDialog}
                          size="lg"
                        />
                      ),
                    }
                  : {
                      noActions: true,
                    })}
              />
            </div>
          )}
          {isError && (
            <div className="mt-4xl mb-4xl">
              <EmptyState
                title={intl.formatMessage({
                  id: "general.couldNotLoadDetails",
                  defaultMessage: "We couldn't load these details",
                })}
                description={intl.formatMessage({
                  id: "general.tryAgainOrCheckBackLater",
                  defaultMessage: "You can try again or check back later.",
                })}
                customIcon={<FeaturedIcon />}
                noActions
              />
            </div>
          )}
          {hasTrackingItems && !isLoading && (
            <Accordion onSelect={handleAccordionSelect} activeKey={activeAccordion || ""}>
              {firstFiveTrackingItems.map((item: TrackingItemProps) => {
                const props = item.history ? { ...item, isExpanded: activeAccordion === item.uuid } : item;
                return (
                  <TrackingItem
                    key={item.uuid}
                    {...props}
                    showAssociatedStopBelowLabel={isRevampEnabled}
                    onEditClick={() => openEditDialog(item.id)}
                    shipmentId={id}
                    refreshTrackingData={(type) => refreshTrackingData(type, queryClient, id)}
                    company_shipper_settings={company_shipper_settings}
                  />
                );
              })}
              {showRestAccordion && hasMoreThanFiveTrackingItems && (
                <>
                  {restOfTrackingItems.map((item: TrackingItemProps) => {
                    const props = item.history ? { ...item, isExpanded: activeAccordion === item.uuid } : item;
                    return (
                      <TrackingItem
                        key={item.uuid}
                        {...props}
                        showAssociatedStopBelowLabel={isRevampEnabled}
                        onEditClick={() => openEditDialog(item.id)}
                        shipmentId={id}
                        refreshTrackingData={(type) => refreshTrackingData(type, queryClient, id)}
                        company_shipper_settings={company_shipper_settings}
                      />
                    );
                  })}
                </>
              )}
            </Accordion>
          )}

          {hasMoreThanFiveTrackingItems && (
            <div
              className={`tracking-item__show-all-button ${showRestAccordion ? "tracking-item__show-all-button__open" : ""}`}
              onClick={() => setShowRestAccordion((prev) => !prev)}
            >
              <ChevronDown />
              <div className="text-sm text-tertiary font-medium">
                {showRestAccordion ? (
                  <FormattedMessage id="general.showLess" defaultMessage="Show less" />
                ) : (
                  <FormattedMessage id="general.showAll" defaultMessage="Show all" />
                )}
              </div>
            </div>
          )}
        </Card>
      </div>
    </>
  );
};

export default Tracking;
